import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const testimonials = [
  {
    id: 1,
    text: "Exceptional technical clarity and execution. The AI finance system delivered exactly what we needed with impressive attention to detail.",
    author: "Sarah Chen",
    role: "Finance Integration",
    date: "March 2026"
  },
  {
    id: 2,
    text: "Outstanding automation work. The YouTube workflow system streamlined our entire content production process and saved countless hours.",
    author: "Michael Rodriguez",
    role: "Content Automation",
    date: "February 2026"
  },
  {
    id: 3,
    text: "Brilliant chatbot implementation. Natural conversations, smart responses, and seamless integration across all our platforms.",
    author: "Emma Thompson",
    role: "Chatbot Development",
    date: "January 2026"
  },
  {
    id: 4,
    text: "The visual design work was phenomenal. Clean, professional, and perfectly aligned with our brand vision.",
    author: "David Park",
    role: "Brand Design",
    date: "December 2025"
  },
  {
    id: 5,
    text: "Impressive coding skills and problem-solving ability. Delivered a complex solution with clean, maintainable code.",
    author: "Lisa Anderson",
    role: "Application Development",
    date: "November 2025"
  },
  {
    id: 6,
    text: "The workflow automation transformed our operations. Everything runs smoother and more efficiently now.",
    author: "James Wilson",
    role: "Process Automation",
    date: "October 2025"
  }
];

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const current = testimonials[currentIndex];

  return (
    <section id="testimonials" className="py-20 px-4 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="mb-16 text-center">
          <p className="text-sm text-black mb-4 font-semibold tracking-wide">/ Client Feedback</p>
          <h2 className="text-5xl md:text-7xl font-bold text-black mb-6">
            What people <span className="text-orange-500">say</span>
          </h2>
          
          {/* Rating Display */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="text-6xl font-bold text-black">4.9</div>
            <div className="text-left">
              <div className="flex gap-1 mb-1">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-6 h-6 text-orange-500 fill-current" viewBox="0 0 20 20">
                    <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                  </svg>
                ))}
              </div>
              <p className="text-sm text-gray-700 font-medium">Based on client reviews</p>
            </div>
          </div>
          
          <p className="text-base text-black max-w-2xl mx-auto font-medium">
            Thoughts from individuals who have experienced the value of collaborative work.
          </p>
        </div>

        {/* Testimonial Card */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl p-12 md:p-16 relative">
            {/* Quote */}
            <div className="mb-8">
              <p className="text-2xl md:text-3xl font-semibold text-black leading-relaxed">
                "{current.text}"
              </p>
            </div>

            {/* Author Info */}
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-xl font-bold text-black">{current.author}</h4>
                <p className="text-sm text-gray-800 font-medium">{current.role}</p>
                <p className="text-xs text-gray-700 mt-1">{current.date}</p>
              </div>

              {/* Navigation */}
              <div className="flex gap-4">
                <button
                  onClick={prevTestimonial}
                  className="w-12 h-12 rounded-full bg-white hover:bg-orange-500 hover:text-white text-black transition-all duration-300 flex items-center justify-center shadow-sm"
                  aria-label="Previous testimonial"
                >
                  <ChevronLeft size={24} />
                </button>
                <button
                  onClick={nextTestimonial}
                  className="w-12 h-12 rounded-full bg-white hover:bg-orange-500 hover:text-white text-black transition-all duration-300 flex items-center justify-center shadow-sm"
                  aria-label="Next testimonial"
                >
                  <ChevronRight size={24} />
                </button>
              </div>
            </div>

            {/* Indicators */}
            <div className="flex justify-center gap-2 mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === currentIndex ? 'w-8 bg-orange-500' : 'w-2 bg-gray-300'
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                >
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;